import {
  Component,
  ElementRef,
  ViewChildren,
  QueryList,
  signal,
  ChangeDetectorRef,
  OnInit,
  OnDestroy,
  inject,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { NgClass, DatePipe } from '@angular/common';
import { NgbDropdownModule, NgbDropdownConfig, NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { FormsModule } from '@angular/forms';
import { TableDetails } from '../table-details.model';
import { Column } from '../column.model';
import { CommonService, ToasterService } from '@anddone/ui-utilities';
import { TableFilterService } from '../table-filter.service';
import { AllyCommonService } from '../../../../../../services/ally-common-service';

@Component({
  selector: 'app-filters',
  imports: [NgClass, NgbDropdownModule, FormsModule, DatePipe],
  templateUrl: './filters.component.html',
  styleUrl: './filters.component.scss',
  providers: [NgbDropdownConfig],
})
export class FiltersComponent implements OnInit, OnDestroy {
  // @Input() InputDataForFilters: any;
  columns: Column[] = [];
  // @Output() OutputDataFromFilters = new EventEmitter();
  globalSearch: any;
  showingSearch = true;
  hasActiveSearch = false;
  hasActiveFilters = false;
  searchTerm = '';
  isLoader = signal<boolean>(true);
  // clearSearchFields: boolean = false;
  emailRegex =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

  private tableFilterSub!: Subscription;
  public tableDetails!: TableDetails;
  config = inject(NgbDropdownConfig);
  public commonService = inject(CommonService);
  public allyCommonService = inject(AllyCommonService);
  private toasterService = inject(ToasterService);
  private tableFilterService = inject(TableFilterService);
  public cdr = inject(ChangeDetectorRef);
  constructor() {
    this.config.autoClose = 'outside';
  }

  @ViewChildren('filterOption') filterOptions!: QueryList<ElementRef>;
  @ViewChildren(NgbDropdown) dropdowns!: QueryList<NgbDropdown>;

  ngOnInit() {
    this.tableFilterSub = this.tableFilterService.clearFiltersEvent?.subscribe(() => {
      this.globalSearch = '';
      this.clearFilters();
    });

    this.tableFilterService.tableFilterColumns?.subscribe((columns: Column[]) => {
      this.columns = columns;
    });

    this.tableFilterService.tableDetails?.subscribe((tableDetails: TableDetails) => {
      this.tableDetails = tableDetails;
    });

    // To manager persistant filters when leaving the
    this.hasActiveFilters = this.tableFilterService.checkTableHasActiveFilters();
    this.hasActiveSearch = this.tableFilterService.checkTableHasActiveSearch();
    this.showingSearch = !this.hasActiveFilters;
    this.globalSearch = this.tableFilterService.getActiveSearchTerm();

    this.allyCommonService.getIsLoader().subscribe((loaderState) => {
      this.isLoader.set(loaderState);
    });
  }

  ngOnDestroy() {
    if (this.tableFilterSub) {
      this.tableFilterSub.unsubscribe();
    }
  }

  setSearchSection() {
    this.showingSearch = true;
  }

  setFilterSection() {
    this.showingSearch = false;
  }

  runSearch() {
    // Search functionality
    if (this.commonService.isValidData(this.globalSearch)) {
      const filterToAdd: any = {
        key: 'SearchText',
        type: 'globalSearch',
        value: this.globalSearch,
      };
      this.tableFilterService.prepForNewSearch();
      this.tableFilterService.addFilter(filterToAdd, null);
      this.hasActiveFilters = false;
      this.hasActiveSearch = true;
      for (const column of this.columns) {
        if (column.display != 'actions' && column.filter != null && column.filter != undefined) {
          column.filter.active = false;
        }
      }
    }
  }

  closeAllDropdowns() {
    this.dropdowns?.forEach((dropdown) => dropdown.close());
  }

  clearAll() {
    this.tableFilterService.clearFilters();
  }

  clearFilters(): void {
    this.hasActiveFilters = false;
    this.hasActiveSearch = false;
    this.searchTerm = '';
    this.closeAllDropdowns();

    setTimeout(() => {
      for (const column of this.columns) {
        const filter = column.filter;

        // Skip if filter doesn't exist
        if (!filter) continue;

        filter.active = false;

        if (filter.type === 'string' || filter.type === 'email') {
          filter.searchString = '';
        } else if (filter.type === 'multipleChoice') {
          const selected = filter.selected ?? [];
          const options = filter.options ?? [];

          for (let i = 0; i < selected.length; i++) {
            selected[i] = false;

            if (options[i]) {
              options[i].checked = false;
            }
          }
        } else if (filter.type === 'amount') {
          if (filter.selectedAmounts) {
            filter.selectedAmounts.exact = null;
            filter.selectedAmounts.min = null;
            filter.selectedAmounts.max = null;
          }
        } else if (filter.type === 'date') {
          if (filter.dateRange) {
            filter.dateRange.from = null;
            filter.dateRange.to = null;
          }

          filter.dateType = 90;
        }
      }
    }, 0);
  }

  setVisible(column: Column) {
    this.tableFilterService.setFilterVisible(column.name);
  }

  onFilterDropdownStateChange(isOpen: boolean) {
    if (isOpen) {
      return;
    }
    for (const column of this.columns) {
      if (!column.filter) continue;

      column.filter.visible = false;
    }
  }

  showingNestedDropdown() {
    for (const column of this.columns) {
      if (!column.filter) continue;

      if (column.filter.visible == true) {
        return true;
      }
    }

    return false;
  }

  toggleDateRange(e: Event) {
    const select = e.target as HTMLSelectElement;
    if (select.value == 'custom') {
      select.classList.add('showing-range');
    } else {
      select.classList.remove('showing-range');
    }
  }

  validateFilterInput(column: Column, input: any) {
    if (!column.filter?.validation) return true;

    for (const validationRule of column.filter.validation) {
      switch (validationRule) {
        case 'required':
          if (!this.hasValidValue(input)) {
            this.triggerValidationNotification('Please enter a value for your filter.');
            return false;
          }
          break;
        case 'email':
          if (!this.isValidEmail(input)) {
            this.triggerValidationNotification('Please enter a valid email address.');
            return false;
          }
          break;
        case 'number':
          if (!this.isValidNumber(input)) {
            this.triggerValidationNotification('Please enter a number.');
            return false;
          }
          break;

        case 'integer':
          if (!this.isValidInteger(input)) {
            this.triggerValidationNotification('Please enter an integer.');
            return false;
          }
          break;

        case 'date':
          if (!this.isValidDate(input)) {
            this.triggerValidationNotification('Please select a valid date.');
            return false;
          }
          break;
      }
    }

    return true;
  }

  /* validation methods */
  hasValidValue(input: any): boolean {
    if (input === null || input === undefined) return false;
    if (typeof input === 'string') return input.trim().length > 0;
    return true;
  }

  isValidEmail(email: string): boolean {
    return this.emailRegex.test(email);
  }

  isValidNumber(value: string): boolean {
    return !isNaN(Number(value)) && value.trim().length > 0;
  }

  isValidInteger(value: string): boolean {
    return /^-?\d+$/.test(value);
  }

  isValidDate(dateString: string): boolean {
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  }
  /* endvalidation methods */

  triggerValidationNotification(message: string) {
    this.toasterService.showError(message, 'full');
  }

  setStringFilter(column: Column, searchTerm: string): boolean {
    if (!this.validateFilterInput(column, searchTerm)) {
      return false;
    }

    // Clear default filters
    this.tableFilterService.clearDefaultFilters();

    const filter = column.filter;

    if (!filter) return false;

    this.hasActiveFilters = true;

    filter.active = true;
    filter.visible = false;
    filter.searchString = searchTerm;

    const filterToAdd = {
      key: column.requestKey,
      type: filter.type,
      value: searchTerm,
    };

    this.hasActiveSearch = false;

    this.tableFilterService.prepForNewFilter();
    this.globalSearch = '';

    this.tableFilterService.addFilter(filterToAdd, column);

    this.closeAllDropdowns();

    return true;
  }

  setCheckboxFilter(column: Column, event: Event) {
    const container = (event.target as HTMLElement).closest('.filter-selection-container');

    if (!container) return;

    // Check to see if there is a default filter set, if so clear it.
    this.tableFilterService.clearDefaultFilters();

    this.hasActiveFilters = true;
    const checkboxes = container.querySelectorAll('input[type="checkbox"]');
    const selectedOptions = Array.from(checkboxes).map(
      (checkbox) => (checkbox as HTMLInputElement).checked
    );

    const filter = column.filter;

    if (!filter?.selected) return;

    for (let i = 0; i < selectedOptions.length; i++) {
      filter.selected[i] = selectedOptions[i];
    }
    filter.active = true;
    const filterToAdd = {
      key: column.requestKey,
      type: filter.type,
      value: selectedOptions,
    };

    this.hasActiveSearch = false;
    this.tableFilterService.prepForNewFilter();
    this.globalSearch = '';
    this.tableFilterService.addFilter(filterToAdd, column);
    this.closeAllDropdowns();
  }

  anyChecked(column: any): boolean {
    return column.filter.options?.some((o: any) => o.checked);
  }

  hasEmptyInput(input: HTMLInputElement, inputs: any = null) {
    if (inputs != null && inputs.length > 0) {
      let hasValue = false;
      for (const field of inputs) {
        if (field.value != '') {
          hasValue = true;
        }
      }
      return hasValue;
    }
    if (input.value == '') {
      return true;
    }
    return false;
  }

  setAmountFilter(column: Column, exact: any, min: any, max: any): void {
    // Clear default filters if any
    this.tableFilterService.clearDefaultFilters();

    const filter = column.filter;

    if (!filter) return;

    this.hasActiveFilters = true;

    filter.active = true;
    filter.visible = false;

    if (!filter.selectedAmounts) {
      filter.selectedAmounts = { exact: null, min: null, max: null };
    }

    if (exact !== null) {
      filter.selectedAmounts.exact = exact;
    }

    if (min !== null) {
      filter.selectedAmounts.min = min;
    }

    if (max !== null) {
      filter.selectedAmounts.max = max;
    }

    this.hasActiveSearch = false;
    this.globalSearch = '';

    this.tableFilterService.prepForNewFilter();

    const filterToAdd = {
      key: column.requestKey,
      type: filter.type,
      exactKey: filter.exactRequestKey,
      exactValue: exact,
      minKey: filter.minRequestKey,
      minValue: min,
      maxKey: filter.maxRequestKey,
      maxValue: max,
    };

    this.tableFilterService.addFilter(filterToAdd, column);

    this.closeAllDropdowns();
  }

  changeAmountSelection(
    amountType: HTMLInputElement,
    min: HTMLInputElement,
    max: HTMLInputElement,
    exact: HTMLInputElement
  ) {
    if (amountType.value == 'exact' && amountType.checked) {
      min.value = '';
      max.value = '';
    } else {
      exact.value = '';
    }
  }

  setDateFilter(column: Column, selection: string, dateFrom: any, dateTo: any): void {
    // Clear default filters
    this.tableFilterService.clearDefaultFilters();

    const filter = column.filter;
    if (!filter) return;

    this.hasActiveFilters = true;
    filter.active = true;
    filter.visible = false;
    filter.dateType = selection;

    if (!filter.dateRange) {
      filter.dateRange = { from: null, to: null };
    }

    if (selection !== 'custom') {
      filter.dateRange.to = new Date();
      const daysBefore = parseInt(selection, 10);
      const fromDate = new Date();
      fromDate.setDate(fromDate.getDate() - daysBefore);

      filter.dateRange.from = fromDate;
    } else {
      filter.dateRange.from = dateFrom;
      filter.dateRange.to = dateTo;
    }

    const filtersToAdd = {
      fromKey: filter.dateFromRequestKey,
      fromValue: this.tableFilterService.formatDate(filter.dateRange.from),
      toKey: filter.dateToRequestKey,
      toValue: this.tableFilterService.formatDate(filter.dateRange.to),
    };

    this.hasActiveSearch = false;
    this.globalSearch = '';
    this.tableFilterService.prepForNewFilter();
    this.tableFilterService.addDateFilters(filtersToAdd, column);
    this.closeAllDropdowns();
  }

  removeFilter(column: Column) {
    const filter = column.filter;
    if (!filter) return;
    if (filter.type == 'string' || filter.type == 'email') {
      filter.searchString = '';
    }

    if (!filter.selectedAmounts) {
      filter.selectedAmounts = { exact: null, min: null, max: null };
    }
    if (filter.type == 'amount') {
      filter.selectedAmounts.exact = null;
      filter.selectedAmounts.min = null;
      filter.selectedAmounts.max = null;
    }

    if (!filter.dateRange) {
      filter.dateRange = { from: null, to: null };
    }
    if (filter.type == 'date') {
      filter.dateRange.from = null;
      filter.dateRange.to = null;
    }
    this.closeAllDropdowns();

    setTimeout(() => {
      const filter = column.filter;
      if (!filter) return;
      filter.active = false;

      if (filter.type === 'date') {
        if (filter.dateFromRequestKey && filter.dateToRequestKey) {
          this.tableFilterService.clearDateFilters(
            filter.dateFromRequestKey,
            filter.dateToRequestKey
          );
        }
      } else if (filter.type === 'amount') {
        if (filter.exactRequestKey && filter.minRequestKey && filter.maxRequestKey) {
          this.tableFilterService.clearAmountFilters(
            filter.exactRequestKey,
            filter.minRequestKey,
            filter.maxRequestKey
          );
        }
      } else {
        if (column.requestKey) {
          this.tableFilterService.clearFilter(column.requestKey);
        }
      }

      if (filter.type === 'multipleChoice') {
        const selected = filter.selected ?? [];
        const options = filter.options ?? [];

        for (let i = 0; i < selected.length; i++) {
          selected[i] = false;
          if (options[i]) {
            options[i].checked = false;
          }
        }
        filter.selected = selected;
        filter.options = options;
      }

      for (const col of this.columns) {
        if (!col.filter) continue;
        if (col.filter.active) {
          return;
        }
      }
      this.hasActiveFilters = false;
      this.hasActiveSearch = false;
    }, 0);
  }

  // If active filter dropdown is closed without being saved, reset inputs to filtered values
  onActiveFilterClose(filter: any, activeFilter: HTMLElement) {
    if (filter.type == 'string' || filter.type == 'email') {
      const input = activeFilter.querySelector<HTMLInputElement>('input');
      if (input) {
        input.value = filter.searchString;
      }
    } else if (filter.type == 'multipleChoice') {
      // let checkboxes = activeFilter.querySelectorAll('input');
      for (let i = 0; i < filter.selected.length; i++) {
        if (filter.selected[i] == true) {
          // checkboxes[i].checked = true;
          filter.options[i].checked = true;
        } else {
          // checkboxes[i].checked = false;
          filter.options[i].checked = false;
        }
      }
    } else if (filter.type == 'amount') {
      const amountFields = activeFilter.querySelectorAll<HTMLInputElement>('input.amount-input');
      const amountTypeRadios =
        activeFilter.querySelectorAll<HTMLInputElement>('input[type="radio"]');
      amountFields[0].value = filter.selectedAmounts.exact ?? '';
      amountFields[1].value = filter.selectedAmounts.min ?? '';
      amountFields[2].value = filter.selectedAmounts.max ?? '';

      if (filter.selectedAmounts.exact == null || filter.selectedAmounts.exact == '') {
        amountTypeRadios[0].checked = false;
        amountTypeRadios[1].checked = true;
      } else {
        amountTypeRadios[0].checked = true;
        amountTypeRadios[1].checked = false;
      }
    } else if (filter.type == 'date') {
      const dateSelect = activeFilter.querySelector('select') as HTMLSelectElement | null;
      const dateFields = activeFilter.querySelectorAll<HTMLInputElement>('input');
      if (dateSelect) {
        dateSelect.value = filter.dateType;
      }
      if (filter.dateType == 'custom') {
        dateFields[0].value = filter.dateRange.from;
        dateFields[1].value = filter.dateRange.to;
      }
    }
  }

  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      const element = document.getElementById('filtersDropdown');
      element?.click();
    }
  }
}
